import { Trash2 } from 'lucide-react'

import type { PackagingDraftItem } from '@/lib/packaging-draft'
import { rowKeyOf } from '@/lib/material-row-id'

import { useAuth } from '@/hooks/use-auth'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    /** true = vật tư nhân viên tự thêm, hiển thị ngang hàng dòng sản phẩm
     *  (không thụt lề mũi tên). Mặc định false = vật tư theo định mức. */
    standalone?: boolean
    row: PackagingDraftItem
    /** Dòng SP đang GỘP giá vật tư → giá hiệu lực = 0 (giá nằm trong giá SP). */
    merged?: boolean
    onQtyChange: (materialId: string, lineKey: string, value: string) => void
    onPriceChange: (materialId: string, lineKey: string, value: string) => void
    onDiscountChange: (
        materialId: string,
        lineKey: string,
        value: string,
    ) => void
    onRemove: (materialId: string, lineKey: string) => void
}

function fmtQty(n: number) {
    return n.toLocaleString('vi-VN', { maximumFractionDigits: 3 })
}

/**
 * PosMaterialRow — dòng vật tư CÓ GIÁ render inline ngay dưới dòng sản phẩm ở màn
 * POS (giỏ hàng). Căn theo layout flex của `PosOrderItemRow`. Mọi dòng đều sửa
 * được SL + đơn giá, hiện tồn/thiếu và xoá được.
 */
export function PosMaterialRow({
    standalone = false,
    row,
    merged = false,
    onQtyChange,
    onPriceChange,
    onDiscountChange,
    onRemove,
}: Props) {
    const { hasPermission } = useAuth()
    const canEditDiscount = hasPermission('sales_orders.edit_discount')
    const effPrice = merged ? 0 : row.unit_price
    const lineAmount =
        effPrice * row.quantity * (1 - (row.discount_percent ?? 0) / 100)
    const shortage = row.quantity > row.available

    return (
        <div
            className={`flex items-center gap-2 px-3 py-1.5 border-b text-sm ${
                shortage ? 'bg-amber-50' : 'bg-muted/20'
            }`}
        >
            {/* # spacer */}
            <span className="w-5 shrink-0" />

            {/* Delete */}
            <span className="w-3.5 shrink-0">
                <button
                    type="button"
                    onClick={() => onRemove(rowKeyOf(row), row.line_key)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`${m.common_aria_delete()} ${row.material_name}`}
                >
                    <Trash2 className="size-3.5" />
                </button>
            </span>

            {/* Tên vật tư + tồn/thiếu */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                    {standalone ? null : (
                        <span className="text-muted-foreground shrink-0">
                            ↳
                        </span>
                    )}
                    <span className="truncate">{row.material_name}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                    {m.packaging_editor_col_available()}:{' '}
                    {fmtQty(row.available)}
                    {shortage ? (
                        <span className="ml-1 text-amber-700">
                            ({m.packaging_editor_badge_shortage()})
                        </span>
                    ) : null}
                </div>
            </div>

            {/* SL */}
            <div className="flex items-center gap-1 shrink-0">
                <input
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    value={row.quantity}
                    onChange={(e) =>
                        onQtyChange(rowKeyOf(row), row.line_key, e.target.value)
                    }
                    className="w-14 text-center border-b border-dashed border-primary/50 bg-transparent text-sm focus:outline-none focus:border-primary"
                    aria-label={`${row.material_name} SL`}
                />
                <span className="text-xs text-muted-foreground">
                    {row.material_unit}
                </span>
                <span className="w-5 shrink-0" />
            </div>

            {/* Đơn giá */}
            <input
                type="number"
                min={0}
                step="1000"
                inputMode="numeric"
                value={effPrice}
                onChange={(e) =>
                    onPriceChange(rowKeyOf(row), row.line_key, e.target.value)
                }
                className="w-20 text-right border-b border-dashed border-primary/50 bg-transparent text-sm focus:outline-none focus:border-primary shrink-0"
                aria-label={`${row.material_name} ${m.order_unit_price()}`}
            />

            {/* CK% */}
            <div className="flex items-center gap-0.5 shrink-0">
                {canEditDiscount ? (
                    <input
                        type="number"
                        min={0}
                        max={100}
                        value={row.discount_percent}
                        onChange={(e) =>
                            onDiscountChange(
                                rowKeyOf(row),
                                row.line_key,
                                e.target.value,
                            )
                        }
                        className="w-10 text-center border-b border-dashed border-primary/50 bg-transparent text-sm focus:outline-none focus:border-primary"
                        aria-label={`${row.material_name} CK %`}
                    />
                ) : (
                    <span className="w-10 text-center text-sm text-muted-foreground">
                        {row.discount_percent}
                    </span>
                )}
                <span className="text-xs text-muted-foreground">%</span>
            </div>

            {/* Thành tiền */}
            <span className="w-24 text-right font-medium shrink-0">
                {formatVND(lineAmount)}
            </span>
        </div>
    )
}
